// 建築レンズ Phase 1（MVP 一段目）の計測（S-061-6 / 設計doc §3-2・§5）。
//
// 計測する 4 種（DEC-055 決定7 と同系・件数積み上げ・率はユーザーに露出させない）:
//   1. lens_landing   … レンズ別ランディングの表示数（建築レンズで「プロジェクト別」が着地した）
//   2. badge_tap      … 未完バッジ付きプロジェクトのタップ数
//   3. rollup_reach   … プロジェクト詳細内のまとめ（S-058 ProjectRollup）への到達数
//   4. empty_rate     … 空率の母数（total=表示プロジェクト数）と分子（empty=未完0件のプロジェクト数）
//
// 永続化方針（DEC-055 決定7 と同系 = Firestore 件数累積）:
//   `users/{uid}/dashboardMetrics/{YYYYMM}` に increment で件数を貯める（本人のみ read/write・
//   firestore.rules で owner スコープ）。率（empty/total 等）はサーバ集計で後から算出し、
//   **ユーザー露出経路には出さない**（DEC-009 / DEC-057 決定内容8）。fire-and-forget で UI を
//   絶対にブロックしない・例外を投げない（usePlan の安全側フォールバックと同じ流儀）。
//
//   テスト・将来の Analytics 配線用に `setLensMetricSink` で書込みシンクを差し替えられる。
//   既定（override 未設定）は Firestore へ書く。
//
// ── 撤退基準（DoD「撤退基準が明文化されている」/ devil-advocate 条件・設計doc §5 Phase2 ゲート3）──
//   観測期間: 建築レンズ向けの内部テスト配信から **2 週間**。
//   Phase2（建築ホーム本体・S-061-7）へ進まない判定線（いずれか）:
//     (a) 観測期間中、建築レンズユーザーの badge_tap → rollup_reach の到達が **ゼロ**
//         （在庫一覧型 UI への需要の最低限の証拠が立たない）。
//     (b) empty_rate（バッジ0件率 = empty/total）が継続して高止まり（仮閾値 80% 超）で、
//         internal 反転（S-061-2）後もバッジが恒常的に空＝死にUI（user-advocate 条件）。
//   いずれかに該当した場合、建築深掘りはマーケ側（S-007 テスター確保）律速と判断し、
//   Phase2 は凍結（COUNCIL-20260611-001 §2-2 / S-061-7 着手ゲート3 未充足のまま据え置き）。
import { doc, setDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

export type LensMetricEvent = 'lens_landing' | 'badge_tap' | 'rollup_reach' | 'empty_rate';

export interface LensMetricPayload {
  // lens_landing: 着地したレンズ。badge_tap / rollup_reach: 対象 projectId。
  lens?: 'focus' | 'construction';
  projectId?: string;
  // empty_rate: 母数（total=全プロジェクト数）と分子（empty=バッジ0件＝未完0のプロジェクト数）。
  total?: number;
  empty?: number;
}

export type LensMetricSink = (event: LensMetricEvent, payload: LensMetricPayload) => void;

// テスト／将来の差し替え用シンク。null（既定）のときは Firestore へ永続化する。
let overrideSink: LensMetricSink | null = null;
export function setLensMetricSink(next: LensMetricSink | null): void {
  overrideSink = next;
}

// 計測ドキュメントの期間キー（UTC 年月 YYYYMM）。月次でドキュメントを分けて肥大を防ぐ。
function periodId(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// 既定シンク: users/{uid}/dashboardMetrics/{YYYYMM} に件数を increment 累積（fire-and-forget）。
// 例外は握り潰す（計測は UI を絶対に壊さない）。
function persistToFirestore(uid: string, event: LensMetricEvent, payload: LensMetricPayload): void {
  try {
    const ref = doc(db, 'users', uid, 'dashboardMetrics', periodId());
    const update: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (event === 'lens_landing') {
      update[`landing_${payload.lens ?? 'unknown'}`] = increment(1);
    } else if (event === 'badge_tap') {
      update.badge_tap = increment(1);
    } else if (event === 'rollup_reach') {
      update.rollup_reach = increment(1);
    } else if (event === 'empty_rate') {
      update.empty_rate_total = increment(payload.total ?? 0);
      update.empty_rate_empty = increment(payload.empty ?? 0);
      update.empty_rate_samples = increment(1);
    }
    void Promise.resolve(setDoc(ref, update, { merge: true })).catch((e) => {
      console.error('[lens-metric] persist failed:', e);
    });
  } catch (e) {
    console.error('[lens-metric] sink error:', e);
  }
}

function emit(uid: string | null | undefined, event: LensMetricEvent, payload: LensMetricPayload): void {
  if (overrideSink) {
    overrideSink(event, payload);
    return;
  }
  if (!uid) return; // uid が無ければ書けない（ログイン前など）。握り潰す。
  persistToFirestore(uid, event, payload);
}

export function recordLensLanding(uid: string | null | undefined, lens: 'focus' | 'construction'): void {
  emit(uid, 'lens_landing', { lens });
}

export function recordBadgeTap(uid: string | null | undefined, projectId: string): void {
  emit(uid, 'badge_tap', { projectId });
}

export function recordRollupReach(uid: string | null | undefined, projectId: string): void {
  emit(uid, 'rollup_reach', { projectId });
}

// 空率: バッジが集計できたタイミングで母数（total）と空件数（empty）を記録する。
export function recordEmptyRate(uid: string | null | undefined, total: number, empty: number): void {
  emit(uid, 'empty_rate', { total, empty });
}
